<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card-title class="d-flex justify-space-between align-center flex-nowrap">
          <div>
            <div class="text-h4 font-weight-bold mb-2">Zones</div>
            <div class="subtitle-2 grey--text">
              Manage and monitor zones within (customer Name Here)
            </div>
          </div>
          <div>
            <v-btn color="success" dark small flat @click="onAddZone">
              <v-icon left size="18">mdi-plus</v-icon>
              Create New
            </v-btn>
          </div>
        </v-card-title>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col v-for="zone in zones" :key="zone.zoneId" cols="12" md="6">
        <v-card elevation="4" class="zone-card" style="height: 500px">
          <v-col class="card-details pa-4 d-flex flex-column" cols="4" style="max-width: 33.3333%">
            <div class="text-h5 font-weight-bold mb-10">
              {{ zone.name }}
            </div>
            <div class="caption grey--text">Updated on</div>
            <div class="subtitle-1 font-weight-bold mb-5">
              {{ formatDate(zone.lastUpdated) }}
            </div>
            <div class="caption grey--text">Type</div>
            <div class="subtitle-1 font-weight-bold mb-5">
              {{ zone.zoneTypeName }}
            </div>
            <div class="caption grey--text">Inspections</div>
            <div class="subtitle-1 font-weight-bold mb-5">
              {{ zone.inspectionCount }}
            </div>
            <div class="caption grey--text">Tasks</div>
            <div class="subtitle-1 font-weight-bold mb-5">
              {{ zone.taskCount }}
            </div>
            <div class="caption grey--text">Actions required</div>
            <div class="subtitle-1 font-weight-bold mb-5">
              {{ zone.findingCount }}
            </div>
            <v-spacer />
            <div>
              <v-btn text small flat class="pa-0" @click="onViewDetails(zone.zoneId)">
                <span class="body-2 font-weight-medium"> View Details → </span>
              </v-btn>
            </div>
          </v-col>

          <v-col cols="8" class="card-map pa-0 d-flex">
            <zone-map-simple
              :center="getZoneCenter(overlays[zone.zoneId])"
              :zoom="12"
              :geojson="overlays[zone.zoneId] || null"
              :show-zoom-controls="false"
            />
          </v-col>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import L from "leaflet";
import ZoneMapSimple from "@/components/ZoneMapSimple.vue";
import { fetchZones, fetchZoneGeoJson } from "@/api/zones";

export default {
  name: "ZoneCards",
  components: { ZoneMapSimple },
  data() {
    return {
      zones: [],
      overlays: {},
    };
  },
  methods: {
    async loadZones() {
      try {
        const zonesData = await fetchZones();
        this.zones = zonesData;
        for (const z of zonesData) {
          try {
            const resp = await fetchZoneGeoJson(z.zoneId);
            if (!resp) {
              this.overlays = { ...this.overlays, [z.zoneId]: null };
              continue;
            }
            // fetchZoneGeoJson already returns parsed GeoJSON (or null)
            const geojson = this.normalizeZoneGeoJSON(resp, z);
            if (geojson && geojson.features) {
              geojson.features.forEach((feature) => {
                feature.properties = feature.properties || {};
                feature.properties.colour = z.colour || "#FF0000";
              });
            }
            // Direct assignment to reactive proxy
            this.overlays = { ...this.overlays, [z.zoneId]: geojson };
          } catch (err) {
            this.overlays = { ...this.overlays, [z.zoneId]: null };
          }
        }
      } catch (err) {
      }
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
    getZoneCenter(geojson) {
      if (!geojson) return [53.41291, -8.24389]; // Center of Ireland
      try {
        const layer = L.geoJSON(geojson);
        const bounds = layer.getBounds();
        if (bounds.isValid()) {
          return [bounds.getCenter().lat, bounds.getCenter().lng];
        }
      } catch (err) {
      }
      return [53.41291, -8.24389]; // Center of Ireland
    },
    onAddZone() {
      this.$router.push({ name: "ZoneCreate" });
    },
    onViewDetails(id) {
      this.$router.push({ name: "ZoneEdit", params: { id } });
    },
    normalizeZoneGeoJSON(raw, z) {
      // parse strings
      let gj = raw;
      if (typeof raw === "string") {
        try {
          gj = JSON.parse(raw);
        } catch (e) {
          return null;
        }
      }

      if (!gj) return null;

      // Wrap single Feature into a FeatureCollection
      if (gj.type === "Feature") {
        gj = { type: "FeatureCollection", features: [gj] };
      }

      if (gj.type === "FeatureCollection" && Array.isArray(gj.features)) {
        gj.features.forEach((f) => {
          f.properties = f.properties || {};
          f.properties.zoneId = z.zoneId; // KEY for highlighting
          f.properties.zoneName = z.ZoneName || z.name || z.zoneName || "";
          f.properties.colour = z.Colour || "#FF0000";
        });
        return gj;
      }

      return null;
    },
  },
  mounted() {
    this.loadZones();
  },
};
</script>

<style scoped>
.zone-card {
  display: flex;
  width: 100%;
  height: 400px;
  border-radius: 8px;
  overflow: hidden;
}
.card-details {
  flex: 0 0 33.3333%;
  display: flex;
  flex-direction: column;
}
.card-map {
  flex: 1 1 0;
  position: relative;
  display: flex;
}
.card-map .map-container {
  width: 100% !important;
  height: 100% !important;
  border-radius: 0;
}
.text-h4 {
  font-size: 1.5rem;
  line-height: 2rem;
}
.font-weight-bold {
  font-weight: 700 !important;
}
.font-weight-medium {
  font-weight: 500 !important;
}
.caption {
  font-size: 0.75rem;
  line-height: 1rem;
}
.grey--text {
  color: #757575 !important;
}
.subtitle-1 {
  font-size: 1rem;
  line-height: 1.5rem;
  color: #212121;
}
.body-2 {
  width: 200px;
  font-size: 0.9rem;
  line-height: 1.25rem;
}
.subtitle-2 {
  font-size: 0.875rem;
}
</style>
